/**
 * Watchlists — operator-scoped lists of instruments with live LTP.
 *
 * Backed by the real backend /watchlists endpoints. The watchlists belong
 * to the calling operator (no cross-operator access — see backend route
 * comments). Live LTP is layered on at render time via useLiveQuote.
 */
import { z } from 'zod';

import { del, get, patch, post, type ApiResult } from './client';

export const watchlistItemSchema = z.object({
  id: z.string(),
  instrument_id: z.string(),
  instrument_symbol: z.string().nullable(),
  instrument_segment_id: z.string().nullable(),
  instrument_tick_size: z.number().nullable().optional(),
  instrument_lot_size: z.number().nullable().optional(),
  ordinal: z.number().int(),
  added_at: z.string(),
});
export const watchlistSchema = z.object({
  id: z.string(),
  name: z.string(),
  item_count: z.number().int(),
  created_at: z.string(),
  updated_at: z.string(),
});
export const watchlistDetailSchema = watchlistSchema.extend({
  items: z.array(watchlistItemSchema),
});
export const watchlistListSchema = z.array(watchlistSchema);
export type Watchlist = z.infer<typeof watchlistSchema>;
export type WatchlistItem = z.infer<typeof watchlistItemSchema>;
export type WatchlistDetail = z.infer<typeof watchlistDetailSchema>;

export const listWatchlists = (
  signal?: AbortSignal,
): Promise<ApiResult<Watchlist[]>> =>
  get('/watchlists', watchlistListSchema, { signal });

export const getWatchlist = (
  id: string,
  signal?: AbortSignal,
): Promise<ApiResult<WatchlistDetail>> =>
  get(
    `/watchlists/${encodeURIComponent(id)}`,
    watchlistDetailSchema,
    { signal },
  );

export const createWatchlist = (name: string) =>
  post('/watchlists', watchlistSchema, { name });

export const renameWatchlist = (id: string, name: string) =>
  patch(
    `/watchlists/${encodeURIComponent(id)}`,
    watchlistSchema,
    { name },
  );

export const deleteWatchlist = (id: string) =>
  del(`/watchlists/${encodeURIComponent(id)}`);

export const addWatchlistItem = (watchlistId: string, instrumentId: string) =>
  post(
    `/watchlists/${encodeURIComponent(watchlistId)}/items`,
    watchlistItemSchema,
    { instrument_id: instrumentId },
  );

export const removeWatchlistItem = (watchlistId: string, instrumentId: string) =>
  del(
    `/watchlists/${encodeURIComponent(watchlistId)}/items/${encodeURIComponent(instrumentId)}`,
  );
